import "./style.scss";

import { subject } from "@casl/ability";
import ActionDownload from "assets/img/svg/actionDownload";
import AlertTriangle from "assets/img/svg/alertTriangle";
import SendEmail from "assets/img/svg/sendEmail";
import AccountingModal from "components/AccountingModal";
import WrongPaymentInfo from "components/Beneficiar/PaymentBlock/WrongPaymentInfo";
import ModalBox from "components/Modal";
import ArticleDrawer from "components/PressManagement/ArticleDrawer";
import { AgreementSignedStatus } from "components/PressManagement/PressManagementTable/utils";
import BlockCards from "components/shared/BlockCards";
import FilterInput from "components/shared/FilterInput";
import LastElementTable from "components/shared/LastElementTable";
import SendEmailModal from "components/shared/SendEmail";
import { completionStatusColorRules, completionStatusOptions } from "containers/ArticleFormContainer/utils";
import type { IBeneficiar } from "containers/Beneficiars/types";
import type { NewAgreementLocationState } from "containers/ContractValidationNew";
import type { ITitle } from "containers/Titles/types";
import { PortalType } from "contexts/portal.context";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useIsMobile } from "hooks/useIsMobile";
import { usePermissions } from "hooks/usePermissions";
import { useSignatureProvider } from "hooks/useSignatureProvider";
import { toggleTheme } from "hooks/useTheme";
import type { CSSProperties } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AgreementSignatureStatus } from "store/agreementsStore";
import type { IArticle } from "store/articleStore";
import {
    ArticleCompletionStatus,
    exportArticles,
    exportBeneficiaryArticles,
    exportParutionAccounting,
    putArticleCompletionStatus,
    putArticlePaid,
    putArticlePaidBeneficiary,
    putArticlePaidBulk,
    setLastSentAtTerms,
} from "store/articleStore";
import { fetchSendStatement } from "store/managementStore";
import type { ICompany } from "store/settingsStore";
import { sendBulkMailWithStatement } from "store/statementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { IBeneficiariesCompanies, TFetchStatus, TPortalType } from "store/types";
import { EPortalType, PaymentStatus } from "store/types";
import { EStatementType } from "types";

import AgreementModal from "../AgreementModal";
import { displayMobileNavBtn } from "../BPMobileNavBtn";
import CompletionDateModal from "../CompletionDateModal";
import BulkActionsMenu from "../LastElementTable/BulkActionsMenu";
import DownloadFilesModal from "../LastElementTable/BulkActionsMenu/DownloadFilesModal";
import type { LastElementTableColumns } from "../LastElementTable/type";
import ModalBase from "../ModalBase";
import StatusSelect from "../StatusSelect";
import type { PaymentOption } from "../StatusSelect/utils";
import { paymentColorRules, paymentOptions } from "../StatusSelect/utils";
import StyledButton from "../StyledButton";
import type { ThreeDotMenuOptions } from "../ThreeDotMenu";
import ThreeDotMenu from "../ThreeDotMenu";
import LastSentChip from "./LastSentChip";

interface IArticlesTable {
    associatedArticles: IArticle[];
    beneficiary?: IBeneficiar;
    publication?: ITitle | undefined;
    articleExportStatus?: TFetchStatus;
    pageType: "publication" | "beneficiary";
    currentCompany?: IBeneficiariesCompanies | ICompany | undefined;
}

export enum EArticleStatus {
    TO_SEND = 0,
    AWAITING_SIGNATURE = 1,
    SIGNED = 2,
}

const ArticlesTable = (props: IArticlesTable) => {
    const {
        associatedArticles: articles,
        beneficiary,
        publication,
        articleExportStatus,
        pageType,
        currentCompany,
    } = props;

    // As portalType "beneficiary" doesn't tell us which type of company it is (mainly for wording), we change portalType according to company linked to title.
    const portal: TPortalType = useContext(PortalType);
    const portalType: TPortalType = useMemo(() => {
        return portal === EPortalType.BENEFICIARY ? currentCompany?.publisherType ?? EPortalType.PRESS : portal;
    }, [portal, currentCompany]);

    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const articleIdFromLocationState = location.state?.articleId;
    const isPromotionFlow = location.state?.promotionFlow;
    const { isMobile, isPhone } = useIsMobile();
    const { getCompanySettings, isCompany } = useCompanySettings();
    const ability = useAbility();

    const parentId = (beneficiary || publication)?.id;
    const titleIdForArticle = (articleId: number) =>
        articles.find((article) => article.id === articleId)?.titleId ?? parentId;
    const [articlePaid, setArticlePaid] = useState(0);
    const [netToPay, setNetToPay] = useState(0);
    const [netPaid, setNetPaid] = useState(0);
    const [totalCost, setTotalCost] = useState(0);
    const [filterValue, setFilterValue] = useState("");
    const [isArticleOpen, setIsArticleOpen] = useState(false);
    const [currentArticle, setCurrentArticle] = useState<IArticle | null>();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [currentArticleForCompletion, setCurrentArticleForCompletion] = useState<{
        completionStatus: ArticleCompletionStatus;
        titleId: number;
        articleId: number;
    } | null>(null);

    const { hasPermissions } = usePermissions();

    const [pendingPaymentArticleId, setPendingPaymentArticleId] = useState<{
        articleId: number;
        titleId: number;
        paidStatus: 2;
    } | null>(null);

    type Modals = "send_email" | "download" | "agreement" | "acounting_export" | "completion_date" | "payment_date";
    const [modalType, setModalType] = useState<Modals | "">("");

    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");
    const usesAgreements = !!getCompanySettings("uses_agreements");

    const activeSignProvider = useActiveSignProvider();
    const { isDocoon } = useSignatureProvider(activeSignProvider.key);

    // Needed when sending articles in bulk for signing.
    const articlesForSending = useMemo(() => {
        return articles.filter((article) => selectedRows.includes(article.id) && article.contractSigned === 0);
    }, [selectedRows, articles]);

    const articleForEmail = useMemo(() => {
        return selectedRows.length === 1
            ? articles.find((article) => article.id === selectedRows[0])
            : ({} as IArticle);
    }, [selectedRows, articles]);

    const seeTranslated = t("Articles.See");

    const blockCardsData = [
        {
            unit: (articles?.length ?? 0) - articlePaid,
            label: t(`Articles.Unpaid_${portalType}`),
            outOf: articles?.length ?? 0,
        },
        {
            unit: formatNumber((netToPay - netPaid) / 100, {
                isCurrency: true,
                always2Digit: true,
            }),
            label: t("Articles.RightToPay"),
            outOf: formatNumber(netToPay / 100, {
                isCurrency: true,
                always2Digit: true,
            }),
        },
        {
            unit: formatNumber(totalCost / 100, {
                isCurrency: true,
                always2Digit: true,
            }),
            label: t("Articles.TotalCost"),
        },
    ];

    const handleExportArticles = () => {
        if (!articles.length) return toast.error(`${t("Articles.NoArticlesToExport")}`);
        publication && articleExportStatus != "pending" && dispatch(exportArticles(publication.id));
        beneficiary &&
            articleExportStatus != "pending" &&
            dispatch(exportBeneficiaryArticles((beneficiary as IBeneficiar).id));
    };

    const handleOpenArticleDetails = (articleId: number, _id2?: any, _type?: any, article?: IArticle) => {
        setCurrentArticle(article);
        setIsArticleOpen(true);
    };

    // Auto-open article drawer when navigated from Command Palette (location.state.articleId)
    useEffect(() => {
        if (!articleIdFromLocationState || !articles.length || isPromotionFlow) return;
        const article = articles.find((item) => item.id === articleIdFromLocationState);
        if (!article) return;
        handleOpenArticleDetails(article.id, undefined, undefined, article);
    }, [articleIdFromLocationState, articles, isPromotionFlow]);

    const normalizedArticle: IArticle[] = useMemo(() => {
        let articlePaid = 0;
        let netToPay = 0;
        let netPaid = 0;
        let totalCost = 0;

        const newArticles =
            (articles.length > 0 &&
                articles
                    .map((article) => {
                        if (article.paidStatus == 2) {
                            articlePaid++;
                            netPaid += article.netToPay;
                        }
                        netToPay += article.netToPay;
                        totalCost += ((article.grossDuty + article.vat - article.withheldVat) * 101.1) / 100;
                        let promotion = {};
                        if (article.id == articleIdFromLocationState && isPromotionFlow)
                            promotion = { promotion: true };
                        return {
                            ...article,
                            action: seeTranslated,
                            validBankInfoRow: article.validBankInfos ? "" : <AlertTriangle />,
                            ...promotion,
                        };
                    })
                    .filter(
                        (item) =>
                            item.name?.toLowerCase().includes(filterValue.toLowerCase()) ||
                            item.titleName?.toLowerCase().includes(filterValue.toLowerCase()) ||
                            item.beneficiary?.toLowerCase().includes(filterValue.toLowerCase()),
                    )) ||
            [];
        setArticlePaid(articlePaid);
        setNetToPay(netToPay);
        setNetPaid(netPaid);
        setTotalCost(totalCost);
        return newArticles;
    }, [articles, filterValue]);

    const handlePaymentStatus = (value: 0 | 1 | 2, id: number) => {
        if (value === 2) {
            setPendingPaymentArticleId({ articleId: id, titleId: titleIdForArticle(id)!, paidStatus: value });
            setModalType("payment_date");
            return;
        }
        const paymentData = { paidStatus: value, paymentDate: null, from: pageType };
        if (publication) dispatch(putArticlePaid({ titleId: titleIdForArticle(id)!, articleId: id, paymentData }));
        else if (beneficiary)
            dispatch(putArticlePaidBeneficiary({ beneficiarId: parentId!, articleId: id, paymentData }));
    };

    const handlePaymentDateSubmit = (date: Date) => {
        if (!pendingPaymentArticleId) return;
        const { articleId, titleId, paidStatus } = pendingPaymentArticleId;
        const paymentData = { paidStatus, paymentDate: date, from: pageType };
        if (publication) dispatch(putArticlePaid({ titleId, articleId, paymentData }));
        else if (beneficiary) dispatch(putArticlePaidBeneficiary({ beneficiarId: parentId!, articleId, paymentData }));
        setModalType("");
        setPendingPaymentArticleId(null);
    };

    const handlePaymentDateClose = () => {
        setModalType("");
        setPendingPaymentArticleId(null);
    };

    const handleSelectedRows = (rows: number[]) => {
        setSelectedRows(rows);
    };

    const handleSendStatements = () => {
        dispatch(
            fetchSendStatement({
                articles: selectedRows,
                host: window.location.origin,
            }),
        );
        setModalType("");
    };

    const handleSendEmail = (
        emails: string[],
        subject: string,
        body: string,
        _ignore?: EStatementType[],
        subjectByLang?: Record<string, string>,
        bodyByLang?: Record<string, string>,
    ) => {
        dispatch(
            sendBulkMailWithStatement({
                articleId: selectedRows,
                mailCc: emails,
                subject,
                body,
                subjectByLang,
                bodyByLang,
            }),
        );
        dispatch(setLastSentAtTerms(selectedRows));
        setModalType("");
    };

    const handleBulkPaymentStatus = (value: 0 | 1 | 2, ids: number[]) => {
        let articlesIdsToUpdate = ids;
        // If we want to pay articles, don't change paid articles (or it will change the payment date)
        if (value === PaymentStatus.PAID) {
            if (!articles.length) return;
            const paidArticlesSet = new Set(
                articles.filter((article) => article.paidStatus === PaymentStatus.PAID).map((article) => article.id),
            );
            articlesIdsToUpdate = ids.filter((id) => !paidArticlesSet.has(id));
        }
        if (!articlesIdsToUpdate?.length) return;
        dispatch(putArticlePaidBulk({ id: articlesIdsToUpdate, status: value }));
    };

    const handleDownloadAccounting = (data: Record<string, any>) => {
        dispatch(exportParutionAccounting(data as any));
    };

    const handleShowAgreementModal = (article?: IArticle) => {
        if (usesAgreementValidation || isDocoon) {
            const {
                id,
                beneficiaryId,
                name: title,
                beneficiary,
                titleId,
                email,
            } = currentArticle || articlesForSending[0] || article;
            const state = {
                relatedResourceData: {
                    relatedResource: "article",
                    relatedResourceId: id,
                    beneficiaryId,
                    title,
                    titleId,
                    beneficiary,
                    email,
                },
                modalType: "single",
                signatureProvider: activeSignProvider.key,
            } as NewAgreementLocationState;
            navigate("/agreements/new", { state });
            return;
        }
        article && setCurrentArticle(article);
        setModalType("agreement");
    };

    const formatArticleName = (value: any, id?: number, currentRow?: any) => {
        return (
            <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
                {value}
                {currentRow?.lastSentAt && <LastSentChip article={currentRow} />}
            </div>
        );
    };

    // Update article completion status
    const updateArticleCompletionStatus = (
        completionStatus: ArticleCompletionStatus,
        titleId: number,
        articleId: number,
    ) => {
        // If changing to "completed", show modal to ask for completion date
        if (completionStatus === ArticleCompletionStatus.COMPLETED) {
            setCurrentArticleForCompletion({ completionStatus, titleId, articleId });
            setModalType("completion_date");
        } else {
            // If changing to "pending", update without date
            dispatch(
                putArticleCompletionStatus({
                    completionStatus,
                    completionDate: null,
                    titleId,
                    articleId,
                    from: "publication-or-beneficiary",
                }),
            );
        }
    };

    const handleCompletionDateSubmit = (completionDate: Date) => {
        if (!currentArticleForCompletion) return;
        dispatch(
            putArticleCompletionStatus({
                ...currentArticleForCompletion,
                completionDate,
                from: "publication-or-beneficiary",
            }),
        );
        setModalType("");
        setCurrentArticleForCompletion(null);
    };

    const handleCompletionDateClose = () => {
        setModalType("");
        setCurrentArticleForCompletion(null);
    };

    const dropdownPaymentOptions = (): PaymentOption[] => {
        // High level access: can set all payment options
        if (ability.can("access", subject("ArticlePaymentOptions", { level: 2 }))) {
            return paymentOptions();
        } else if (ability.can("access", subject("ArticlePaymentOptions", { level: 1 }))) {
            return [
                { label: t("Articles.NotPaid"), value: PaymentStatus.NOT_PAID },
                { label: t("Articles.ToPay"), value: PaymentStatus.TO_PAY },
            ];
        }

        return [];
    };

    const disablePaymentSelect = (value: PaymentStatus) => {
        if (value === PaymentStatus.PAID) {
            return ability.cannot("access", subject("ArticlePaymentOptions", { level: 2 }));
        } else {
            return ability.cannot("access", subject("ArticlePaymentOptions", { level: 1 }));
        }
    };

    const hideValidBankInfoColumn =
        portal === EPortalType.BENEFICIARY || articles.some((a) => !!a.validBankInfos) || pageType !== "beneficiary";

    const articlesColumns: LastElementTableColumns[] = [
        {
            id: portal === EPortalType.BENEFICIARY ? "name" : publication ? "beneficiary" : "titleName",
            label: t("Articles.Name"),
            align: "left",
            avatar: false,
            subtitle: portal === EPortalType.BENEFICIARY ? undefined : "name",
            sort: true,
            format: portal === EPortalType.BENEFICIARY ? undefined : formatArticleName,
        },
        {
            id: "contractSigned",
            label: t("Articles.Contract"),
            align: isMobile ? "right" : "left",
            hidden: isPhone,
            format: (value: any, id, currentRow) => {
                if (portal === EPortalType.BENEFICIARY && !value) return "-";
                return (
                    <AgreementSignedStatus
                        value={value}
                        article={currentRow}
                        usesAgreementValidation={usesAgreementValidation}
                    />
                );
            },
            sort: true,
        },
        {
            id: "netToPay",
            label: t("Articles.NetToPay"),
            align: isMobile ? "right" : "left",
            format: (value: any) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
            sort: true,
        },
        {
            id: "paymentDate",
            label: t("Articles.PaymentDate"),
            align: "left",
            format: (value: any, id, currentRow) =>
                currentRow.paidStatus === PaymentStatus.PAID
                    ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } })
                    : "-",
            hidden: portal === EPortalType.BENEFICIARY,
        },
        {
            id: "paidStatus",
            label: t("Articles.PaidStatus"),
            align: "left",
            minWidth: "150px",
            format: (value: any, id) => (
                <StatusSelect
                    value={value}
                    options={paymentOptions()}
                    dropdownOptions={dropdownPaymentOptions()}
                    onChange={(newPaymentValue: 0 | 1 | 2) => handlePaymentStatus(newPaymentValue, id!)}
                    disabled={disablePaymentSelect(value)}
                    colorRules={paymentColorRules}
                />
            ),
            sort: true,
            hidden: portal === EPortalType.BENEFICIARY,
        },
        {
            id: "completionStatus",
            label: t("Articles.CompletionStatusLabel"),
            align: "left",
            format: (value, id, currentRow) => {
                const { titleId, id: articleId } = currentRow;
                return (
                    <StatusSelect
                        value={value}
                        onChange={(completionStatus) =>
                            updateArticleCompletionStatus(completionStatus, titleId, articleId)
                        }
                        options={completionStatusOptions()}
                        disabled={!hasPermissions(["article:edit-completion-status"])}
                        minWidth="123.88px"
                        colorRules={completionStatusColorRules}
                        hideColorStatus // delete this prop if we want to show color status
                    />
                );
            },
            hidden: portal === EPortalType.BENEFICIARY,
        },
        {
            id: "completionDate",
            label: t("Articles.CompletionDate"),
            align: "left",
            sort: true,
            format: (value: any, id, currentRow) =>
                currentRow.completionStatus === ArticleCompletionStatus.COMPLETED && value
                    ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } })
                    : "-",
            hidden: portal === EPortalType.BENEFICIARY,
        },
        {
            id: "validBankInfo",
            label: "",
            align: "center",
            minWidth: "50px",
            format: (value: any) => <WrongPaymentInfo />,
            hidden: hideValidBankInfoColumn,
        },
        {
            id: "action",
            label: "",
            align: "center",
            minWidth: "100px",
            hidden: isMobile,
            format: (value: any) => <div style={{ color: "#275AB0", fontWeight: "600" }}>{value}</div>,
        },
    ];

    /**
     * Options used in three-dot menu
     */
    const options: ThreeDotMenuOptions[] = [
        {
            id: 1,
            name: t("Payment.ExportAccounting"),
            icon: <></>,
            action: () => setModalType("acounting_export"),
        },
    ];

    const renderPromotionFlow = (article: IArticle) => (
        <div className="promotion-content article-detail-content-signature" style={{ marginTop: "0" }}>
            {article.contractSigned == 0 && (
                <>
                    <SmallBody color="info">{t("Publications.NextStep")}</SmallBody>
                    <StyledButton buttonType="outlined" size="small" onClick={() => handleShowAgreementModal(article)}>
                        <SendEmail />
                        {t("Articles.SendSignature")}
                    </StyledButton>
                </>
            )}
            {article.contractSigned == 1 && (
                <div className="article-detail-content-signature signing" style={{ marginTop: "0" }}>
                    <div className="dot"></div>
                    <span>{t("Articles.ToSign")}</span>
                </div>
            )}
            {article.contractSigned == 2 && (
                <div className="article-detail-content-signature signed" style={{ marginTop: "0" }}>
                    <div className="dot"></div>
                    <span>
                        {article?.contractSignedDate &&
                            t("Articles.Signed") +
                                " " +
                                formatLocaleDateString({
                                    date: article?.contractSignedDate,
                                    options: { numeric: true, timeZone: "UTC" },
                                })}
                    </span>
                </div>
            )}
            {article.contractSigned == AgreementSignatureStatus.REFUSED && (
                <StyledButton buttonType="outlined" size="small" onClick={() => handleShowAgreementModal(article)}>
                    <SendEmail />
                    {t("Articles.SendSignature")}
                </StyledButton>
            )}
        </div>
    );

    useEffect(() => {
        if (isMobile) {
            displayMobileNavBtn(!isArticleOpen);
            if (isArticleOpen) toggleTheme("default");
            else toggleTheme("light-grey");
        }
    }, [isArticleOpen]);

    const showBulkPaymentStatusSelect = () => {
        return ability.can("update", "ArticlePayment");
    };

    return (
        <div style={publication ? { borderTop: "1px solid #eaeaea" } : {}}>
            {["audiovisual", "press"].includes(portal) && <BlockCards data={blockCardsData} compact />}
            <ModalBox
                onClose={() => setModalType("")}
                open={modalType === "acounting_export"}
                mainTitle={t("Accounting.Export")}
                subTitle={t("Accounting.ExportSubtitle")}
            >
                <AccountingModal
                    origin="parution"
                    titleId={publication?.id as number}
                    onSubmit={handleDownloadAccounting}
                />
            </ModalBox>
            <div className="article-list articles-tab">
                <div className="article-list-top">
                    <h3>
                        {publication
                            ? t(`Articles.ArticlesFromPublication_${portalType}`)
                            : t(`Articles.ArticlesBy_${portalType}`)}{" "}
                        {beneficiary?.name} {beneficiary?.surname}
                    </h3>
                    <div className="article-list-right">
                        <FilterInput
                            filterValue={filterValue}
                            setFilterValue={setFilterValue}
                            size="small"
                            style={{ ".MuiInputBase-root": { height: "100%" } } as CSSProperties}
                        />
                        {publication && ["audiovisual", "press"].includes(portal) && (
                            <>
                                <div className="article-list-download" onClick={handleExportArticles}>
                                    <ActionDownload />
                                </div>
                                <ThreeDotMenu options={options} />
                            </>
                        )}
                    </div>
                </div>
                <BulkActionsMenu
                    selectedRowsCount={selectedRows.length}
                    show={selectedRows.length > 0}
                    style={{ marginTop: "16px" }}
                >
                    <StyledButton buttonType="secondary" size="small" onClick={() => setModalType("download")}>
                        {t("ShowStatements.Download")}
                    </StyledButton>
                    <StyledButton buttonType="secondary" size="small" onClick={() => setModalType("send_email")}>
                        {t("Articles.SendStatement", { count: selectedRows.length })}
                    </StyledButton>
                    {usesAgreements && (
                        <StyledButton
                            buttonType="secondary"
                            size="small"
                            onClick={() => handleShowAgreementModal()}
                            disabled={
                                articlesForSending.length === 0 || (usesAgreementValidation && selectedRows.length > 1)
                            }
                        >
                            {t("Agreements.BtnSendContratsToSign")}
                        </StyledButton>
                    )}
                    {showBulkPaymentStatusSelect() && (
                        <StatusSelect
                            options={paymentOptions()}
                            fullWidth
                            dropdownOptions={dropdownPaymentOptions()}
                            onChange={(newPaymentValue: 0 | 1 | 2) =>
                                handleBulkPaymentStatus(newPaymentValue, selectedRows)
                            }
                            colorRules={paymentColorRules}
                        />
                    )}
                </BulkActionsMenu>
                <LastElementTable
                    columns={articlesColumns}
                    data={normalizedArticle}
                    onShowAll={() => navigate("/titles")}
                    type={t(`Articles.AddArticle_${portalType}`)}
                    onRowClick={handleOpenArticleDetails}
                    onRowClickId="id"
                    max={-1}
                    allowInfiniteScroll={isMobile}
                    dataType="articles"
                    stickyFooter={isMobile ? false : true}
                    collapseOn={() => "promotion"}
                    collapseContent={(row) => renderPromotionFlow(row)}
                    selectable={[EPortalType.AUDIOVISUAL as TPortalType, EPortalType.PRESS].includes(portal)}
                    onSelectRows={handleSelectedRows}
                    collapseSelectedBackground="#eef3fd"
                    collapseContentBackground="#eef3fd"
                    portalType={portalType}
                />
            </div>

            {/** Article details drawer */}
            <ArticleDrawer
                open={isArticleOpen}
                onClose={() => setIsArticleOpen(false)}
                articlesFromTable={normalizedArticle || articles}
                currentArticle={currentArticle}
                setCurrentArticle={setCurrentArticle}
                from={pageType}
                handleShowAgreementModal={handleShowAgreementModal}
            />

            {[EPortalType.AUDIOVISUAL as TPortalType, EPortalType.PRESS].includes(portal) &&
                modalType === "send_email" && (
                    <SendEmailModal
                        isModalOpen={modalType === "send_email"}
                        onClose={() => setModalType("")}
                        sendEmail={handleSendEmail}
                        isPress
                        isBulk={selectedRows.length === 1 ? false : true}
                        showGross={false}
                        termData={
                            articleForEmail && {
                                beneficiaryId: articleForEmail.beneficiaryId,
                                author: articleForEmail.beneficiary,
                                title: articleForEmail.name,
                                email: articleForEmail.email,
                                precount: `${articleForEmail.precount}`,
                                issue: articleForEmail.titleName,
                                contractSigned: articleForEmail.contractSigned,
                            }
                        }
                    />
                )}

            {/** Download Statements Modal */}
            <DownloadFilesModal
                open={modalType === "download"}
                onClose={() => setModalType("")}
                selectedItemsCount={selectedRows.length}
                modalTitle={t("Management.DownloadStatement")}
                handleDownload={handleSendStatements}
            />
            {/** Completion Date Modal */}
            <ModalBase
                modalTitle={t("Articles.CompletionDateModalTitle")}
                modalSubtitle={t("Articles.CompletionDateModalSubtitle")}
                open={modalType === "completion_date"}
                onClose={handleCompletionDateClose}
            >
                <CompletionDateModal
                    onSubmit={handleCompletionDateSubmit}
                    onClose={handleCompletionDateClose}
                    defaultDate={new Date()}
                />
            </ModalBase>
            {/** Payment Date Modal */}
            <ModalBase
                modalTitle={t("Articles.PaymentDateModalTitle")}
                modalSubtitle={t("Articles.PaymentDateModalSubtitle")}
                open={modalType === "payment_date"}
                onClose={handlePaymentDateClose}
            >
                <CompletionDateModal
                    onSubmit={handlePaymentDateSubmit}
                    onClose={handlePaymentDateClose}
                    defaultDate={new Date()}
                    label={t("Articles.PaymentDate")}
                />
            </ModalBase>
            {modalType === "agreement" && (
                <AgreementModal
                    open={modalType === "agreement"}
                    articles={articlesForSending}
                    article={articlesForSending.length === 1 ? articlesForSending[0] : (currentArticle as IArticle)}
                    inBulk={selectedRows.length > 1}
                    onClose={() => setModalType("")}
                    from={portalType as "audiovisual" | "press"}
                    pageType={pageType}
                    relatedResource="article"
                />
            )}
        </div>
    );
};

export default ArticlesTable;
